import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage, type MessageDescriptor } from 'react-intl';
import { faPrint } from '@fortawesome/pro-solid-svg-icons/faPrint';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';

import { downloadDocumentRequest } from '../../../../../../../store/ltl/ltl.actions';
import type { LtlShipment } from '@shipstation/client-common/types/ltl';
import { confirmationCardMessages } from './confirmation-card-messages';

import style from './confirmation-cards.module.less';

const FREIGHT_DOCUMENT_TYPES = [
  { type: 'BILL_OF_LADING', labelKey: 'billOfLading' },
  { type: 'PACKING_LIST', labelKey: 'packingList' },
  { type: 'PALLET_LABEL', labelKey: 'palletLabels' },
  { type: 'QUOTE', labelKey: 'rateQuote' },
] as const;

type DocumentsCardProps = {
  shipmentDetails: LtlShipment;
};

export const DocumentsCard = ({ shipmentDetails }: DocumentsCardProps) => {
  const dispatch = useDispatch();

  const handlePrintDocument = useCallback(
    (documentType: string) => {
      if (shipmentDetails.productTransactionId) {
        dispatch(downloadDocumentRequest(shipmentDetails.productTransactionId, documentType));
      }
    },
    [dispatch, shipmentDetails.productTransactionId],
  );

  return (
    <div className={style['card']}>
      <div className={style['card-header']}>
        <Text isBold textType={TextType.H4} value={confirmationCardMessages.documentsHeader} />
      </div>
      <div className={style['card-body']}>
        <FlexColumn gap={Gap.Space4}>
          {FREIGHT_DOCUMENT_TYPES.map((doc) => (
            <DocumentRow
              key={doc.type}
              documentType={doc.type}
              label={confirmationCardMessages[doc.labelKey]}
              onPrint={handlePrintDocument}
            />
          ))}
        </FlexColumn>
      </div>
    </div>
  );
};

type DocumentRowProps = {
  documentType: string;
  label: MessageDescriptor;
  onPrint: (documentType: string) => void;
};

const DocumentRow = ({ documentType, label, onPrint }: DocumentRowProps) => {
  const handlePrint = useCallback(() => {
    onPrint(documentType);
  }, [onPrint, documentType]);

  return (
    <div className={style['document-row']}>
      <Text className={style['document-name']} textType={TextType.Paragraph} value={label} />
      <Button onClick={handlePrint} theme={THEME_CONSTANTS.link}>
        <FontAwesomeIcon icon={faPrint} />
        <FormattedMessage {...confirmationCardMessages.printDocument} />
      </Button>
    </div>
  );
};
